import React, { useMemo, useState } from 'react';
import { CheckCircle2, Eye, EyeOff, Loader2, Lock, Mail, User as UserIcon } from 'lucide-react';

import CarteAuth, { ACCENT, BoutonPrincipal, Champ, ENCRE, Message } from '../components/CarteAuth';
import { adminApi } from '../services/api.jsx';

/* ═══════════════════════════════════════════════════════════════════════════
   COMPLÉTER SES ACCÈS

   Quatre champs : prénoms, nom, mot de passe, confirmation. Deux ont été
   retirés, et pour de bonnes raisons.

   L'adresse e-mail. Elle est déjà en base — c'est par elle que les accès ont
   été transmis. La faire ressaisir n'ajoutait qu'un risque de faute de frappe
   rendant le compte injoignable. Elle est donc affichée, pas redemandée. Seul
   cas où le champ réapparaît : un compte créé sans adresse réelle, qui n'a
   qu'une adresse technique interne.

   L'ancien mot de passe. Il n'y en a pas : la personne arrive ici par un
   lien à usage unique, qui a ouvert la session en cours. Le seul mot de
   passe de ce compte est celui qu'elle choisit sur cet écran.
   ═══════════════════════════════════════════════════════════════════════════ */

function mesurer(mot) {
  if (!mot) return { score: 0, libelle: '', couleur: '#E3E9E4' };
  let score = 0;
  if (mot.length >= 8) score += 1;
  if (mot.length >= 12) score += 1;
  if (mot.length >= 16) score += 1;
  const familles = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/]
    .filter((f) => f.test(mot)).length;
  if (familles >= 2) score += 1;
  if (familles >= 3) score += 1;
  const echelle = [
    { libelle: 'Très faible', couleur: '#F04438' },
    { libelle: 'Faible', couleur: '#F04438' },
    { libelle: 'Moyen', couleur: '#F79009' },
    { libelle: 'Bon', couleur: '#F79009' },
    { libelle: 'Solide', couleur: ACCENT },
    { libelle: 'Excellent', couleur: ACCENT },
  ];
  return { score, ...echelle[Math.min(score, 5)] };
}

export default function CompleteProfile({ user, onComplete }) {
  const adresseConnue = (user?.email || '').trim();
  const adresseProvisoire = !adresseConnue || adresseConnue.endsWith('@incomplete.local');

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: adresseProvisoire ? '' : adresseConnue,
    new_password: '',
    confirm_password: '',
  });
  const [visible, setVisible] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const force = useMemo(() => mesurer(form.new_password), [form.new_password]);
  const concordent = form.new_password && form.new_password === form.confirm_password;

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    setErreur(null);
    if (!concordent) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setEnvoi(true);
    try {
      onComplete(await adminApi.completeProfile({
        username: user.username,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        new_password: form.new_password,
      }));
    } catch (e) {
      setErreur(e?.message || 'La mise à jour du profil a échoué.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <CarteAuth
      surtitre="Première connexion"
      titre="Complétez vos accès"
      description={`Vous êtes connecté en tant que ${user?.username}. Il ne reste qu'à vous présenter et à choisir votre mot de passe.`}
    >
      <form onSubmit={soumettre} className="space-y-5">
        {!adresseProvisoire && (
          <div className="rounded-pastille px-4 py-3 text-[13px] leading-relaxed"
               style={{ background: '#F7F9F8', border: '1px solid #E3E9E4' }}>
            <span className="text-[var(--text-muted)]">Adresse enregistrée</span>
            <div className="font-medium mt-0.5" style={{ color: ENCRE }}>{adresseConnue}</div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-[var(--text-main)]">Prénoms</label>
            <Champ icone={UserIcon} type="text" required autoComplete="given-name"
                   placeholder="Ada" value={form.first_name}
                   onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-[var(--text-main)]">Nom</label>
            <Champ type="text" required autoComplete="family-name"
                   placeholder="Lovelace" value={form.last_name}
                   onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
        </div>

        {adresseProvisoire && (
          <div className="space-y-1.5">
            <label className="text-[12.5px] font-medium text-[var(--text-main)]">
              Adresse e-mail professionnelle
            </label>
            <Champ icone={Mail} type="email" required autoComplete="email"
                   placeholder="vous@impactlab-cilis.org" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
              Votre compte a été créé sans adresse. Celle-ci servira à vous
              joindre et à récupérer votre accès en cas d'oubli.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-[12.5px] font-medium text-[var(--text-main)]">Mot de passe</label>
          <div className="relative">
            <Champ icone={Lock} type={visible ? 'text' : 'password'} required
                   autoComplete="new-password" placeholder="Au moins 8 caractères"
                   value={form.new_password}
                   onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
            <button type="button" onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? 'Masquer' : 'Afficher'}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]
                               hover:text-[var(--text-main)] transition-colors">
              {visible ? <EyeOff className="w-[17px] h-[17px]" />
                       : <Eye className="w-[17px] h-[17px]" />}
            </button>
          </div>
          {form.new_password && (
            <div className="pt-1.5">
              <div className="flex gap-1" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} className="h-[3px] flex-1 rounded-controle transition-colors"
                        style={{ background: i < force.score ? force.couleur : '#E8ECEA' }} />
                ))}
              </div>
              <p className="text-[12px] mt-1.5" style={{ color: force.couleur }}>
                {force.libelle}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="text-[12.5px] font-medium text-[var(--text-main)]">Confirmation</label>
          <div className="relative">
            <Champ icone={Lock} type={visible ? 'text' : 'password'} required
                   autoComplete="new-password" placeholder="Saisissez-le à nouveau"
                   value={form.confirm_password}
                   onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} />
            {concordent && (
              <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2
                                       w-[17px] h-[17px]" style={{ color: ACCENT }}
                            aria-label="Les mots de passe correspondent" />
            )}
          </div>
          {form.confirm_password && !concordent && (
            <p className="text-[12px] text-[var(--alerte)]">Les deux saisies diffèrent.</p>
          )}
        </div>

        <Message>{erreur}</Message>

        <BoutonPrincipal type="submit" disabled={envoi || !concordent} aria-busy={envoi || undefined}>
          {envoi ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Enregistrement…</> : 'Accéder à la console'}
        </BoutonPrincipal>
      </form>
    </CarteAuth>
  );
}
